import { useEffect } from 'react';

/**
 * Height of the window that the on-screen keyboard covers, in CSS pixels.
 *
 * Chrome on Android resizes the page itself (`interactive-widget=resizes-content` in index.html),
 * so the visual viewport stays as tall as the window and this returns 0. iOS Safari keeps the
 * window height and only shrinks the visual viewport; what is left below it is the keyboard.
 * Small differences (toolbar animations, rounding) are ignored.
 */
export function keyboardInset(windowHeight: number, viewportHeight: number, viewportTop: number) {
  const covered = Math.round(windowHeight - viewportHeight - viewportTop);
  return covered > 80 ? covered : 0;
}

const VARIABLE = '--nv-keyboard-inset';

/**
 * Keeps `--nv-keyboard-inset` on <html> up to date, so the app shell (nivio-ui.css `.nv-shell`)
 * ends where the keyboard starts and the composer stays in view while typing.
 */
export function useKeyboardInset() {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // A pinch zoom also shrinks the visual viewport; that is not a keyboard.
        const inset =
          viewport.scale > 1.01
            ? 0
            : keyboardInset(window.innerHeight, viewport.height, viewport.offsetTop);
        if (inset > 0) root.style.setProperty(VARIABLE, `${inset}px`);
        else root.style.removeProperty(VARIABLE);
      });
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      root.style.removeProperty(VARIABLE);
    };
  }, []);
}
